/* eslint-disable react-hooks/exhaustive-deps */
import "./styles.css";
import { ReactNode, useContext, useEffect, useState } from "react";
import {
  Box,
  Grid,
  useTheme,
  styled,
  Dialog,
  DialogContent,
  Divider,
  Typography,
} from "@mui/material";
import { EntityContext } from "../../../../contexts/EntityContext";
import GcomLogo from "../../../../assets/img/dashboard-title.svg";
import metadeskLogo from "../../../../assets/img/metadesk.svg";
import summitLogo from "../../../../assets/img/summit.svg";
import campaignLogo from "../../../../assets/img/campaign-tool.svg";
import { ReactComponent as RightArrow } from "../../../../assets/img/long-arrow-right.svg";
import apiLogo from "../../../../assets/img/api-platform.svg";
import {
  clearLocalStorageData,
  getLocalStorageData,
} from "../../../../shared/utils/localStorage";
import { useLocation, useNavigate } from "react-router-dom";
import useMutation from "../../../../shared/components/hooks/useMutation";
import UserAccount from "../../../../shared/components/userAccount";
// import HouseLogo from "../../../../assets/img/house.svg";
import CentralizedLogo from "../../../../assets/img/centralized.svg";
import useQuery from "../../../../shared/components/hooks/useQuery";

const CustomDialog = styled(Dialog)({
  "& .MuiBackdrop-root": {
    backgroundColor: "rgba(111, 111, 111, 0.5)",
    backdropFilter: "blur(8px)",
  },
  "& .MuiDialog-paper": {
    backgroundColor: "#2A2A2A",
    borderRadius: "32px",
    border: "1px solid #D0C6FF",
    background: "rgba(167, 167, 167, 0.3)",
    backdropFilter: "blur(29px)",
    minWidth: "500px",
  },
  "& .MuiDialogContent-root": {
    padding: "24px",
    color: "#FFFFFF",
  },
  "& h3": {
    color: "rgba(0, 0, 0, 0.9)",
    fontSize: "24px",
    fontStyle: "normal",
    fontWeight: 300,
    lineHeight: "normal",
    borderBottom: " 1px solid rgba(255, 255, 255, 0.3)",
    paddingBottom: "32px",
    "& span": {
      color: "rgba(0, 0, 0, 0.9)",
      fontSize: "24px",
      fontStyle: "normal",
      fontWeight: 500,
      lineHeight: "normal",
      textTransform: "capitalize",
    },
  },
  "& .cancelButtons": {
    color: "#FFF",
    textAlign: "center",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "normal",
    textTransform: "capitalize",
    background: "transparent",
    border: "none",
    cursor: "pointer",
  },
  "& .modalLogoutButton": {
    textAlign: "center",
    borderRadius: "32px",
    border: "1px solid #227079",
    background: "#227079",
    boxShadow: "0px 6px 25px -8px #227079",
    color: "rgba(255, 255, 255, 1)",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "normal",
    textTransform: "capitalize",
    cursor: "pointer",
    width: "100%",
    padding: "18px 32px",
    "&:hover": {
      borderRadius: "32px",
      border: "1px solid #227079",
      background: "#227079",
      boxShadow: "0px 6px 25px -8px #227079",
      color: "#FFFFFF",
    },
  },
});

interface SidebarLayoutProps {
  children?: ReactNode;
}

const icons = [
  { image: metadeskLogo, title: "Metadesk", key: "METADESK" },
  { image: summitLogo, title: "Summit", key: "SUMMIT" },
  { image: campaignLogo, title: "Campaign tool", key: "CAMPAIGN" },
  { image: apiLogo, title: "API Platform", key: "APILAYER" },
  {
    image: CentralizedLogo,
    title: "Centralized Platform",
    key: "CENTRALIZED",
  },
];

const Dashboard = () => {
  const { setEntityName } = useContext(EntityContext);
  const location = useLocation();

  useEffect(() => {
    setEntityName({ entityName: "Dashboard", entityTitle: "Dashboard" });
  }, []);

  const { data: userData } = useQuery("application/myApplication");

  const localStorageData = getLocalStorageData("userData");
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [selectedIcon, setSelectedIcon] = useState<string>("");
  const navigate = useNavigate();
  const { modifyData } = useMutation();
  console.log(localStorageData);
  const applicationAccessKeys =
    localStorageData?.applications?.map(
      (item: any) => item?.application?.name || item?.name,
    ) || [];

  const uniqueAccessKeys = [
    ...new Set(
      applicationAccessKeys.map((name: string) => name?.toUpperCase().trim()),
    ),
  ];
  const userType = localStorageData?.userType;

  const filteredIcons = icons.filter((icon) => {
    const access = uniqueAccessKeys.includes(icon.key);
    if (
      (icon.key === "CAMPAIGN" || icon.key === "APILAYER") &&
      !(userType === "Admin" || userType === "SuperAdmin")
    ) {
      return false;
    }

    return access;
  });

  const handleIconClick = async (title: string, event?: React.MouseEvent) => {
    event?.preventDefault();
    event?.stopPropagation();

    const token = getLocalStorageData("token");
    setSelectedIcon(title);

    let redirectUrl = "";
    if (title === "Summit") {
      if (userType === "Admin" || userType === "SuperAdmin") {
        redirectUrl = `${
          process.env.REACT_APP_SUMMIT_ADMIN_URL
        }?token=${encodeURIComponent(token)}`;
      } else {
        redirectUrl = `${
          process.env.REACT_APP_SUMMIT_CLIENT_API_URL
        }?token=${encodeURIComponent(token)}`;
      }
    } else if (title === "Metadesk") {
      if (userType === "Admin" || userType === "SuperAdmin") {
        redirectUrl = `${
          process.env.REACT_APP_METADESK_ADMIN_FRONTEND
        }?token=${encodeURIComponent(token)}`;
      } else {
        redirectUrl = `${
          process.env.REACT_APP_METADESK_CLIENT_FRONTEND
        }?token=${encodeURIComponent(token)}`;
      }
    } else if (title === "API Platform") {
      if (userType === "Admin" || userType === "SuperAdmin") {
        redirectUrl = `${
          process.env.REACT_APP_API_LAYER_ADMIN_FRONTEND
        }?token=${encodeURIComponent(token)}`;
      }
    } else if (title === "Campaign tool") {
      if (userType === "Admin" || userType === "SuperAdmin") {
        redirectUrl = `${
          process.env.REACT_APP_CAMPAIGN_ADMIN_FRONTEND
        }?token=${encodeURIComponent(token)}`;
      }
    } else if (title === "Centralized Platform") {
      if (userType === "Admin" || userType === "SuperAdmin") {
        redirectUrl = `${
          process.env.REACT_APP_CENTRALIZED_FRONTEND_URL
        }?token=${encodeURIComponent(token)}`;
      }
    }

    if (redirectUrl) {
      // Opens in same tab
      // window.location.href = redirectUrl;
      // Or if you don't want back button history:
      window.location.replace(redirectUrl);
      // clearLocalStorageData();
    }
  };

  const handleLogoutProfileClick = () => {
    setOpen(true);
  };

  const handleLogout = async () => {
    setOpen(false);
    // Implement your logout logic here
    const response = await modifyData(`/auth/logout`, "post");
    if (response) {
      clearLocalStorageData();
      navigate("/login");
    }
  };

  useEffect(() => {
    const token = getLocalStorageData("token");
    const userData = getLocalStorageData("userData");

    if (!token) {
      navigate("/login");
    } else if (!userData) {
      handleLogout();
    }
  }, [location.pathname]);

  const email = localStorageData?.emailIdentifier;
  const avatarName = email?.charAt(0)?.toUpperCase();
  return (
    <>
      <Box className="dashboard-main-box">
        <Box className="dashboard-menu-container">
          <Grid
            container
            sx={{ justifyContent: "space-between", alignItems: "center" }}
            mt={1}
          >
            <Grid item md={3.5} xs={8}>
              <img
                src={GcomLogo}
                alt=""
                style={{ width: "360px", height: "auto" }}
              />
            </Grid>
            <Grid item md={2} xs={2}>
              <UserAccount
                userInitial={avatarName}
                onLogout={handleLogoutProfileClick}
              />
            </Grid>
          </Grid>
        </Box>

        <Divider variant="middle" sx={{ margin: "0 2%" }} />

        <Box
          sx={{
            margin: "0% 10%",
            alignItems: "center",
            display: "flex",
            justifyContent: "center",
            flexGrow: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              // alignItems: "stretch",
              alignItems: "center",
              gap: 2,
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography
                className="dashboard-login-text"
                sx={{ whiteSpace: "pre-line" }}
              >
                Choose an app to {"\n"} Login
              </Typography>
              <RightArrow style={{ marginTop: "10%" }} width={62} />
            </Box>

            {/* <Box
              sx={{
                display: { xs: "none", md: "flex" },
                alignItems: "stretch",
                px: 2,
              }}
            > */}
            <Divider
              orientation="vertical"
              flexItem
              className="dashboard-second-divider"
            />
            {/* </Box> */}

            <Grid
              container
              spacing={2}
              sx={{ flex: 1 }}
              alignItems="center"
              justifyContent="center"
            >
              {filteredIcons.map((icon, index) => (
                <Grid item xs={12} md={6} key={index}>
                  {/* aspect-ratio: 310 / 165;
  max-width: 310px !important; */}
                  {/* aspect-ratio: 260 / 130;
  max-width: 260px !important; */}
                  <Box
                    className="dashboard-app-box"
                    sx={{ maxWidth: "310px", aspectRatio: "310/165" }}
                    onClick={(e) => handleIconClick(icon.title, e)}
                  >
                    <img
                      src={icon.image}
                      alt={icon.title}
                      style={{ width: "75%", height: "auto" }}
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>
      </Box>

      <CustomDialog open={open} onClose={() => setOpen(false)}>
        <DialogContent>
          {/* <h2>{selectedIcon}</h2> */}
          <h3>
            Would you like to <span>log out</span> now?
          </h3>
          <Grid
            container
            sx={{
              justifyContent: "space-around",
              alignItems: "center",
              mt: 4,
            }}
          >
            <Grid item xs={3}>
              <button className="cancelButtons" onClick={() => setOpen(false)}>
                Cancel
              </button>
            </Grid>
            <Grid item xs={5.5}>
              <button
                className="modalLogoutButton"
                onClick={() => handleLogout()}
              >
                Logout
              </button>
            </Grid>
          </Grid>
        </DialogContent>
      </CustomDialog>
    </>
  );
};

export default Dashboard;
